import { FunctionComponent, ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { useCheckoutSelector } from '../reducers/root';
import PreQuoteTitle from '../shared/delivery/PreQuoteTitle';
import ExternallyArrangedTitle from '../shared/delivery/ExternallyArrangedTitle';
import LocalPickupTitle from '../delivery/LocalPickupTitle';

import { CheckoutReviewStepDeliveryView_item$key } from './__generated__/CheckoutReviewStepDeliveryView_item.graphql';
import { CheckoutReviewStepDeliveryView_transaction$key } from './__generated__/CheckoutReviewStepDeliveryView_transaction.graphql';
import { CheckoutReviewStepDeliveryView_deliveryOptions$key } from './__generated__/CheckoutReviewStepDeliveryView_deliveryOptions.graphql';

const itemFragment = graphql`
    fragment CheckoutReviewStepDeliveryView_item on Item {
        vertical
    }
`;

const transactionFragment = graphql`
    fragment CheckoutReviewStepDeliveryView_transaction on Transaction {
        ...PreQuoteTitle_transaction
        sku {
            ...PreQuoteTitle_sku
        }
    }
`;

const deliveryOptionsFragment = graphql`
    fragment CheckoutReviewStepDeliveryView_deliveryOptions on ShipmentQuote @relay(plural: true) {
        ...PreQuoteTitle_shipmentQuote
        serviceId
    }
`;

type Props = {
    item: CheckoutReviewStepDeliveryView_item$key;
    transaction: CheckoutReviewStepDeliveryView_transaction$key;
    deliveryOptions: CheckoutReviewStepDeliveryView_deliveryOptions$key;
};

export const CheckoutReviewStepDeliveryView: FunctionComponent<Props> = props => {
    const item = useFragment(itemFragment, props.item);
    const transaction = useFragment(transactionFragment, props.transaction);
    const deliveryOptions = useFragment(deliveryOptionsFragment, props.deliveryOptions);

    const offerCurrency = useCheckoutSelector(state => state.transaction.offerCurrency);
    const shipmentType = useCheckoutSelector(state => state.transaction.shipmentType);
    const shipmentQuoteId = useCheckoutSelector(state => state.transaction.shipmentQuoteId);

    function getTitle(): ReactNode {
        const vertical = item?.vertical;
        const preQuote = (deliveryOptions || []).find(
            option => option?.serviceId === shipmentQuoteId
        );
        const isQuoteNeeded = shipmentType === 'FIRSTDIBS_FACILITATED' && !shipmentQuoteId;
        const isLocalPickup = shipmentType === 'LOCAL_PICKUP';
        const isExternallyArranged = shipmentType === 'EXTERNALLY_ARRANGED';

        if (isQuoteNeeded) {
            return (
                <FormattedMessage
                    id="checkout.CheckoutReviewStepDeliveryView.quoteNeededTitleReview"
                    defaultMessage="{vertical, select, art {Gallery} other {Seller}} will send shipping quote within 48 hours"
                    values={{ vertical }}
                />
            );
        } else if (isLocalPickup) {
            return <LocalPickupTitle />;
        } else if (isExternallyArranged) {
            return <ExternallyArrangedTitle />;
        } else if (preQuote) {
            return (
                <PreQuoteTitle
                    shipmentQuote={preQuote}
                    sku={transaction?.sku}
                    transaction={transaction}
                    offerCurrency={offerCurrency}
                />
            );
        } else {
            return null;
        }
    }

    return (
        <div>
            <p data-tn="delivery-method-title">{getTitle()}</p>
        </div>
    );
};
